import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { useContactForm, ContactValues } from './useContactForm';
import { useLanguage } from '../../hooks/useLanguage';
import { serviceOptions } from '../../data/services';
import { ui } from '../../data/translations';
const inputBase = 'mt-1.5 block w-full rounded-md border bg-white px-4 py-3 text-base text-ink placeholder:text-steel/60 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gold';
const methods: ContactValues['contactMethod'][] = ['phone', 'email'];
export function ContactForm() {
  const {
    values,
    errors,
    status,
    update,
    submit,
    reset
  } = useContactForm();
  const {
    t
  } = useLanguage();
  const err = (key: keyof ContactValues) => {
    const code = errors[key];
    return code ? t(ui.form.errors[code]) : undefined;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit();
  };
  return <div className="rounded-lg border border-black/10 bg-white p-6 shadow-sm sm:p-8" data-id="element-23" data-mp="2y3398grz2kmb">
      <AnimatePresence mode="wait" initial={false} data-id="element-24" data-mp="2t51ta4puf1f2">
        {status === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex flex-col items-center py-12 text-center" role="status" data-id="element-25" data-mp="374kuseo5feie">
            <CheckCircle2Icon className="h-14 w-14 text-gold" aria-hidden="true" data-id="element-26" data-mp="3c34juny1h6ez" />
            <h3 className="mt-5 font-display text-2xl font-bold text-navy" data-id="element-27" data-mp="3qr8ur5quodn7">{t(ui.form.successTitle)}</h3>
            <p className="mt-3 max-w-sm text-steel" data-id="element-28" data-mp="3lus5u9bttv92">{t(ui.form.successBody)}</p>
            <button type="button" onClick={reset} className="mt-8 h-12 rounded-md border-2 border-navy px-6 font-display text-sm font-semibold text-navy transition-colors duration-150 hover:bg-navy hover:text-white" data-id="element-29" data-mp="3n2o4ak44djk">
              {t(ui.form.another)}
            </button>
          </motion.div> : <motion.form key="form" onSubmit={handleSubmit} noValidate initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} data-id="element-30" data-mp="31hfk7jglqvyz">
            <h3 className="font-display text-2xl font-bold text-navy" data-id="element-31" data-mp="3b6z5swvp93xj">{t(ui.form.title)}</h3>
            <p className="mt-1 text-sm text-steel" data-id="element-32" data-mp="3g4qy3s09as29">{t(ui.form.subtitle)}</p>

            <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-33" data-mp="3l6g957nhb5qc">
              <Field label={t(ui.form.name)} name="name" error={err('name')} required className="sm:col-span-2" data-id="element-34" data-mp="3p707gejsgfe2">
                <input id="name" type="text" autoComplete="name" value={values.name} onChange={e => update('name', e.target.value)} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} className={`${inputBase} ${errors.name ? 'border-red-500' : 'border-black/15'}`} data-id="element-35" data-mp="1qrvq0zet3i92" />
              </Field>
              <Field label={t(ui.form.email)} name="email" error={err('email')} required data-id="element-36" data-mp="19ibdvmycrgep">
                <input id="email" type="email" autoComplete="email" value={values.email} onChange={e => update('email', e.target.value)} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} className={`${inputBase} ${errors.email ? 'border-red-500' : 'border-black/15'}`} data-id="element-37" data-mp="268byd9rovaq7" />
              </Field>
              <Field label={t(ui.form.phone)} name="phone" error={err('phone')} required data-id="element-38" data-mp="lq4d8bllgn9h">
                <input id="phone" type="tel" autoComplete="tel" value={values.phone} onChange={e => update('phone', e.target.value)} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'phone-error' : undefined} className={`${inputBase} ${errors.phone ? 'border-red-500' : 'border-black/15'}`} data-id="element-39" data-mp="gvgt0oxmif0p" />
              </Field>
              <Field label={t(ui.form.service)} name="service" className="sm:col-span-2" data-id="element-40" data-mp="2negy8j9531gi">
                <select id="service" value={values.service} onChange={e => update('service', e.target.value)} className={`${inputBase} border-black/15`} data-id="element-41" data-mp="2sev7g4mh8hg4">
                  {serviceOptions.map(opt => <option key={opt.id} value={opt.id} data-id="element-42" data-mp="12zezgskah1rt">
                      {t(opt.label)}
                    </option>)}
                </select>
              </Field>
              <Field label={t(ui.form.description)} name="description" className="sm:col-span-2" data-id="element-43" data-mp="zhhmbnp5wmjx">
                <textarea id="description" rows={4} placeholder={t(ui.form.descriptionPlaceholder)} value={values.description} onChange={e => update('description', e.target.value)} className={`${inputBase} resize-y border-black/15`} data-id="element-44" data-mp="vgfjyxzy7udq" />
              </Field>

              <fieldset className="sm:col-span-2" data-id="element-45" data-mp="bu9f3086b3f9">
                <legend className="text-sm font-medium text-ink" data-id="element-46" data-mp="brdgd9oi4apr">{t(ui.form.method)}</legend>
                <div className="mt-2 flex gap-3" data-id="element-47" data-mp="qjxg00cg002g">
                  {methods.map(method => {
                const active = values.contactMethod === method;
                return <label key={method} className={`flex h-12 flex-1 cursor-pointer items-center justify-center rounded-md border-2 text-sm font-semibold transition-colors duration-150 focus-within:ring-2 focus-within:ring-gold ${active ? 'border-navy bg-navy text-white' : 'border-black/15 text-ink hover:border-navy/40'}`} data-id="element-48" data-mp="3u54moggcbr1j">
                        <input type="radio" name="contactMethod" value={method} checked={active} onChange={() => update('contactMethod', method)} className="sr-only" data-id="element-49" data-mp="26vbg4lh8c0py" />
                        {method === 'phone' ? t(ui.form.methodPhone) : t(ui.form.methodEmail)}
                      </label>;
              })}
                </div>
              </fieldset>
            </div>

            {status === 'error' && <p role="alert" className="mt-5 text-sm text-red-600" data-id="element-50" data-mp="2zjqrmy15k0wj">
                {t(ui.form.error)}
              </p>}

            <button type="submit" disabled={status === 'submitting'} className="mt-7 inline-flex h-14 w-full items-center justify-center gap-2 rounded-md bg-gold font-display text-base font-semibold text-navy-deep transition-colors duration-150 hover:bg-gold-dark disabled:cursor-not-allowed disabled:opacity-70" data-id="element-51" data-mp="39qtdhmlvjpgg">
              {status === 'submitting' && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-52" data-mp="2qw4oi9eippmx" />}
              {status === 'submitting' ? t(ui.form.sending) : t(ui.form.submit)}
            </button>
          </motion.form>}
      </AnimatePresence>
    </div>;
}
type FieldProps = {
  label: string;
  name: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
};
function Field({
  label,
  name,
  error,
  required,
  className = '',
  children
}: FieldProps) {
  return <div className={className} data-id="element-53" data-mp="b5ii7qt06s6y">
      <label htmlFor={name} className="text-sm font-medium text-ink" data-id="element-54" data-mp="ufurlb568fzq">
        {label}
        {required && <span className="text-red-600" data-id="element-55" data-mp="yhy4m47hnsn4"> *</span>}
      </label>
      {children}
      {error && <p id={`${name}-error`} className="mt-1.5 text-sm text-red-600" data-id="element-56" data-mp="kr35vkqperx9">
          {error}
        </p>}
    </div>;
}